import type { Registration } from "@/models/Registration";
import { createSnackbarNotification } from "@frontiers/brink-ui";
import ErrorMessages from "~/helpers/ErrorMessages";
import type { PositionResponse } from "~/models/PositionResponse";
import { TimerService } from "~/services/TimerService";
import { useStep0Store } from "~/stores/step0Store";
import { ErrorCode } from "~/enum/ErrorCode";
import { useRegistrationStore } from "~/stores/registrationStore";
import type { GetInvitationDataResponse } from "~/models/GetInvitationDataResponse";
import type { GetRegistrationDataResponse } from "~/models/GetRegistrationDataResponse";
import type { RegistrationStep0 } from "@/models/RegistrationStep0";
import { useJourneyConfigurationStore } from "~/stores/journeyConfigurationStore";
import { QueryStringHelper } from "~/helpers/QueryStringHelper";

const timerService = new TimerService();

export class RegistrationService {
    public static async register(registration: Registration): Promise<void> {
        const step0Store = useStep0Store();
        const registrationStore = useRegistrationStore();
        try {
            const response = await fetch("/api/onboarding-ui/v1/registration/register", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(registration)
            });
            const currentParams = QueryStringHelper.GetAllQueryStringParameters();

            if (response.ok) {
                timerService.resetTimer();
                const successResult = await response.json();
                registrationStore.verificationEmail = registration.email;
                registrationStore.initial = registration.firstName[0] + registration.lastName[0];
                if (successResult.HasBeenActivated) {
                    if (successResult.IsInvitation && successResult.JourneyId > 0) {
                        const journeyConfigurationStore = useJourneyConfigurationStore();
                        await journeyConfigurationStore.fetchData(successResult.JourneyId);

                    }

                    window.location.href = successResult.ReturnUrl;
                }
                else {
                    if (currentParams == '') {
                        await navigateTo("/register/code-validation");
                    }
                    else {
                        await navigateTo("/register/code-validation?" + currentParams);
                    }
                }

            } else {

                const errorResult = await response.json();
                const errorCode = errorResult.Code;
                const errorMessage = ErrorMessages.getMessage(errorCode);

                if (errorCode === ErrorCode.UserSuspended || errorCode === ErrorCode.AlreadyExistingAccount || errorCode === ErrorCode.UserNotLinkedToTenant) {
                    step0Store.emailAddress = registration.email;
                    if (currentParams == '') {
                        await navigateTo("/people/login");
                    }
                    else {
                        await navigateTo("/people/login?" + currentParams);
                    }
                    const errorMessage =
                        ErrorMessages.getMessage(ErrorCode.AlreadyExistingAccount);
                    createSnackbarNotification({
                        message: errorMessage,
                        type: "error",
                        showCloseIcon: true
                    });
                }
                else if (errorCode == ErrorCode.AccountInactiveOrNotVerified) {
                    registrationStore.verificationEmail = registration.email;
                    if (currentParams == '') {
                        await navigateTo("/register/code-validation");
                    }
                    else {
                        await navigateTo("/register/code-validation?" + currentParams);
                    }
                }
                else {
                    createSnackbarNotification({
                        message: errorMessage,
                        type: "error",
                        autoDismiss: true,
                        showCloseIcon: true
                    });
                    const error = new Error(errorMessage);
                    newrelic.noticeError(error);
                }
            }
        } catch (e) {
            console.error("Error posting registration: " + e);
            throw e;
        }
    }

    public static async getPositions(): Promise<PositionResponse> {
        try {
            const response = await fetch("/api/onboarding-ui/v1/registration/positions", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const responseData: PositionResponse = await response.json();
            return responseData;
        } catch (error) {
            console.error("Error fetching positions: " + error);
            throw error;
        }
    }

    public static async registerStep0(registrationStep0: RegistrationStep0): Promise<void> {
        try {
            const response = await fetch("/api/onboarding-ui/v1/registration/register/step0", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(registrationStep0)
            });

            const currentParams = QueryStringHelper.GetAllQueryStringParameters();

            if (response.ok) {
                if (currentParams == '') {
                    await navigateTo("/register/step-1");
                }
                else {
                    await navigateTo("/register/step-1?" + currentParams);
                }
            } else {

                const errorResult = await response.json();
                const errorCode = errorResult.Code;
                const errorMessage = ErrorMessages.getMessage(errorCode);

                if (errorCode === ErrorCode.UserSuspended || errorCode === ErrorCode.AlreadyExistingAccount || errorCode === ErrorCode.UserNotLinkedToTenant) {
                    if (currentParams == '') {
                        await navigateTo("/people/login");
                    }
                    else {
                        await navigateTo("/people/login?" + currentParams);
                    }
                    const errorMessage =
                        ErrorMessages.getMessage(ErrorCode.AlreadyExistingAccount);
                    createSnackbarNotification({
                        message: errorMessage,
                        type: "error",
                        showCloseIcon: true
                    });
                }
                else {
                    createSnackbarNotification({
                        message: errorMessage,
                        type: "error",
                        autoDismiss: true,
                        showCloseIcon: true
                    });
                    const error = new Error(errorMessage);
                    newrelic.noticeError(error);
                }

            }
        } catch (e) {
            console.error("Error posting registration step 0: " + e);
            throw e;
        }
    }

    public static async getInvitationData(requestAuthToken: string): Promise<GetInvitationDataResponse | null> {
        try {

            if (requestAuthToken == null || requestAuthToken.length == 0) {
                const journeyConfigurationStore = useJourneyConfigurationStore();
                await journeyConfigurationStore.fetchData(null);

                return null;
            }

            const response = await fetch("/api/onboarding-ui/v1/invitation/invitation-info?RequestAuthToken=" + requestAuthToken, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const responseData: GetInvitationDataResponse = await response.json();

            const journeyConfigurationStore = useJourneyConfigurationStore();
            await journeyConfigurationStore.fetchData(responseData.journeyId);

            return responseData;
        } catch (error) {
            console.error("Error fetching invitation data: " + error);
            throw error;
        }
    }

    public static async getRegistrationData(): Promise<GetRegistrationDataResponse> {
        try {
            const response = await fetch("/api/onboarding-ui/v1/registration/data");
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const responseData: GetRegistrationDataResponse = await response.json();
            return responseData;
        } catch (e) {
            console.error("Error fetching user registration data: " + e);
            throw e;
        }
    }
}